// ============================================================================
// DuplicatePointsZones ViewModel
// ============================================================================

import { useState, useCallback, useMemo } from "react";
import type {
	DuplicatePointsZonesProps,
	DuplicatePointsZonesViewModel,
	MapFeatureData,
} from "./DuplicatePointsZones.types";
import type { DuplicateGroup } from "../../WizardContainer";
import { DUPLICATE_GROUP_COLORS } from "../../shared/constants";

// Point sizes for stacking (largest at bottom)
const POINT_SIZES = [24, 18, 12, 8, 6];

// Zone hatch angles (opposite directions)
const ZONE_HATCH_ANGLES = [45, 135, 90, 0];

/**
 * ViewModel hook for DuplicatePointsZones component
 */
export function useDuplicatePointsZonesViewModel(props: DuplicatePointsZonesProps): DuplicatePointsZonesViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteFeature } = props;

	// Track which features are hidden on the map
	const [hiddenFeatureIds, setHiddenFeatureIds] = useState<Set<string>>(new Set());
	// State for delete confirmation dialog
	const [deleteFeatureInfo, setDeleteFeatureInfo] = useState<{
		groupId: string;
		featureIndex: number;
		featureType: "point" | "zone";
	} | null>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Create unique feature ID
	const createFeatureId = useCallback((groupId: string, featureIndex: number): string => {
		return `${groupId}-${featureIndex}`;
	}, []);

	// Toggle feature visibility
	const toggleFeatureVisibility = useCallback((featureId: string) => {
		setHiddenFeatureIds((prev) => {
			const next = new Set(prev);
			if (next.has(featureId)) {
				next.delete(featureId);
			} else {
				next.add(featureId);
			}
			return next;
		});
	}, []);

	// Show all features
	const showAllFeatures = useCallback(() => {
		setHiddenFeatureIds(new Set());
	}, []);

	// Hide all features
	const hideAllFeatures = useCallback(() => {
		if (!currentIssue) return;
		const allIds = new Set<string>();
		for (const group of currentIssue.duplicateGroups) {
			for (let i = 0; i < group.features.length; i++) {
				allIds.add(createFeatureId(group.id, group.featureIndices[i]));
			}
		}
		setHiddenFeatureIds(allIds);
	}, [currentIssue, createFeatureId]);

	// Get display name for a feature
	const getFeatureDisplayName = useCallback((group: DuplicateGroup, index: number): string => {
		const feature = group.features[index];
		const featureIndex = group.featureIndices[index];

		// Try to get a name from properties
		const props = feature.properties;
		if (props) {
			// Common name fields
			for (const field of ["name", "Name", "NAME", "id", "Id", "ID", "SAMPLE_ID", "SampleID"]) {
				if (props[field]) {
					return String(props[field]);
				}
			}
		}

		// Fallback to type + index
		return `${group.type === "point" ? "Point" : "Zone"} #${featureIndex + 1}`;
	}, []);

	// Get total duplicate count
	const getTotalDuplicateCount = useCallback((): number => {
		if (!currentIssue) return 0;
		return currentIssue.duplicateGroups.reduce((sum, group) => sum + group.features.length, 0);
	}, [currentIssue]);

	// Get color for a position within group (same position = same color across all groups)
	const getPositionColor = useCallback((positionIndex: number): string => {
		return DUPLICATE_GROUP_COLORS[positionIndex % DUPLICATE_GROUP_COLORS.length];
	}, []);

	// Generate map features data
	const mapFeatures = useMemo((): MapFeatureData[] => {
		if (!currentIssue) return [];

		const features: MapFeatureData[] = [];

		currentIssue.duplicateGroups.forEach((group) => {
			group.features.forEach((feature, i) => {
				const featureIndex = group.featureIndices[i];
				// Color by position within group (not by group) so users can identify
				// which features to delete across all groups (e.g., "delete all the blue ones")
				const color = getPositionColor(i);
				features.push({
					feature,
					groupId: group.id,
					featureIndex,
					isDuplicate: true,
					color,
					size: group.type === "point" ? POINT_SIZES[Math.min(i, POINT_SIZES.length - 1)] : undefined,
					hatchAngle: group.type === "zone" ? ZONE_HATCH_ANGLES[i % ZONE_HATCH_ANGLES.length] : undefined,
				});
			});
		});

		return features;
	}, [currentIssue, getPositionColor]);

	// Handle delete feature with confirmation
	const handleDeleteFeatureClick = useCallback(
		(groupId: string, featureIndex: number, featureType: "point" | "zone") => {
			setDeleteFeatureInfo({ groupId, featureIndex, featureType });
		},
		[],
	);

	const handleConfirmDelete = useCallback(() => {
		if (deleteFeatureInfo && currentIssue) {
			onDeleteFeature(currentIssue.job.id, deleteFeatureInfo.featureType, deleteFeatureInfo.featureIndex);
		}
		setDeleteFeatureInfo(null);
	}, [deleteFeatureInfo, currentIssue, onDeleteFeature]);

	const handleCancelDelete = useCallback(() => {
		setDeleteFeatureInfo(null);
	}, []);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		hiddenFeatureIds,
		toggleFeatureVisibility,
		showAllFeatures,
		hideAllFeatures,
		deleteFeatureInfo,
		handleDeleteFeatureClick,
		handleConfirmDelete,
		handleCancelDelete,
		getFeatureDisplayName,
		getTotalDuplicateCount,
		mapFeatures,
		getPositionColor,
	};
}
